import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useCanGoBack, useRouter } from "@tanstack/react-router";
import { GameShelf } from "@/features/games/components/GameShelf";
import { GameShelfSkeleton } from "@/features/games/components/GameShelfSkeleton";
import { normalizePileId } from "@/features/games/piles";
import { gamesQuery } from "@/features/games/queries";

interface GamesSearch {
  /** The pile currently open over the shelf, if one is. */
  pile?: string;
}

export const Route = createFileRoute("/_authenticated/games")({
  validateSearch: (search: Record<string, unknown>): GamesSearch => {
    const pile = normalizePileId(search.pile);
    return { ...(pile && { pile }) };
  },
  loader: ({ context: { queryClient } }) => queryClient.query(gamesQuery),
  head: () => ({ meta: [{ title: "Games · Pax.co" }] }),
  pendingComponent: GameShelfSkeleton,
  component: GamesPage,
});

function GamesPage() {
  const { pile } = Route.useSearch();
  const navigate = Route.useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const { data: games } = useSuspenseQuery(gamesQuery);

  return (
    <GameShelf
      games={games}
      openPileId={pile ?? null}
      // The shelf is still there underneath, so opening a pile is not
      // allowed to move it.
      onOpenPile={next => void navigate({ search: { pile: next }, resetScroll: false })}
      // Leaving a pile you opened here is the browser's back button in a
      // different place, the same as every other way out of a screen. A pile
      // that was already open when this screen arrived has no opening to
      // undo, so that one goes to the shelf.
      onClosePile={undo =>
        undo && canGoBack
          ? router.history.back()
          : void navigate({ search: {}, resetScroll: false })
      }
    />
  );
}
